import { useEffect, useRef, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { changeUserSuspension, getUserById, getUserSuspension } from '../api/authAdmin';
import { getApiEnvironment } from './apiEnvironment';
import { getUserId } from './userBanActions';
import { canManageSuspension, isSuspensionFormValid, suspensionPublicReason } from './userSuspensionActions';
import { t } from './i18n';

export function useUserSuspensionActions({ token, apiEnvironment, role, cacheSession, onUnauthorized }) {
  const queryClient = useQueryClient();
  const [dialog, setDialog] = useState(null);
  const [isPending, setPending] = useState(false);
  const generation = useRef(0);
  const dialogScope = useRef(null);
  const scopeMatches = () => dialogScope.current?.environment === apiEnvironment && dialogScope.current?.token === token && dialogScope.current?.role === role && dialogScope.current?.cacheSession === cacheSession;
  useEffect(() => { generation.current += 1; dialogScope.current = null; setDialog(null); }, [apiEnvironment, token, role, cacheSession]);
  const open = async (user) => {
    if (!canManageSuspension(user, apiEnvironment) || isPending || !token) return;
    const environment = apiEnvironment;
    const epoch = ++generation.current;
    dialogScope.current = { environment, token, role, cacheSession };
    setDialog({ stage: 'loading', user, environment });
    try {
      const [freshUser, restriction] = await Promise.all([getUserById(getUserId(user), token), getUserSuspension(getUserId(user), token)]);
      if (epoch !== generation.current || !scopeMatches() || getApiEnvironment() !== environment) return;
      setDialog({ stage: 'form', user: freshUser, environment, restriction, action: 'apply', days: 1, category: '', reason: '', notes: '' });
    } catch (err) {
      if (epoch !== generation.current || !scopeMatches()) return;
      if (err?.status === 401) onUnauthorized?.();
      setDialog({ stage: 'error', user, environment, message: err?.data?.msg || t('user.suspension.loadError') });
    }
  };
  const close = () => { if (!isPending) { generation.current += 1; dialogScope.current = null; setDialog(null); } };
  const update = (changes) => setDialog((previous) => ({ ...previous, ...changes, requestId: undefined }));
  const review = () => {
    if (!dialog || !isSuspensionFormValid(dialog)) return;
    setDialog({ ...dialog, stage: 'confirm', requestId: crypto.randomUUID(), estimatedExpiry: new Date(Date.now() + Number(dialog.days) * 86400000).toISOString() });
  };
  const confirm = async () => {
    if (!dialog || dialog.stage !== 'confirm' || dialog.stale || isPending || !scopeMatches() || dialog.environment !== apiEnvironment || getApiEnvironment() !== dialog.environment) return;
    const snapshot = dialog;
    const operationScope = dialogScope.current;
    setPending(true);
    try {
      const result = await changeUserSuspension(getUserId(snapshot.user), snapshot.action, {
        duration_days: snapshot.action === 'apply' ? Number(snapshot.days) : 0,
        category: snapshot.action === 'apply' ? snapshot.category : undefined,
        reason: suspensionPublicReason(snapshot), notes: snapshot.notes.trim(),
        expected_restriction_id: snapshot.restriction?.current?.active ? snapshot.restriction.current.restriction_id : '',
      }, snapshot.requestId, token);
      ['users', 'usersSummary', 'userById', 'userBanLogs', 'user-detail'].forEach((key) => queryClient.invalidateQueries({ queryKey: [key] }));
      if (operationScope === dialogScope.current && scopeMatches() && getApiEnvironment() === snapshot.environment) setDialog({ ...snapshot, stage: 'success', result });
    } catch (err) {
      if (err?.status === 401) onUnauthorized?.();
      if (operationScope === dialogScope.current && scopeMatches() && getApiEnvironment() === snapshot.environment) setDialog({ ...snapshot, stage: 'confirm', error: err?.status === 409 ? t('user.suspension.stale') : err?.data?.msg || t('user.suspension.saveError'), stale: err?.status === 409 });
    } finally { setPending(false); }
  };
  return { dialog: scopeMatches() ? dialog : null, open, close, update, review, confirm, isPending };
}
